import { useTranslation } from "react-i18next";
import { useApiClient } from "../data/clients/ApiClient";
import { useProfileContext } from "../contexts/ProfileContext";
import { useOrganizationImpersonationContext } from "../contexts/OrganizationImpersonationContext";
import { Role } from "../core/models/entities/Role";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
  showWarningToast,
} from "../presentation/components/Toast/Toast";
import {
  classifySheetSizeRequestOutcome,
  isSheetSizeRequestNotFound,
  OVERSIZE_SHEET_REQUEST_TIMEOUT_MS,
  OversizeLogoInfo,
  resolveSheetSizeRequestOrgId,
  resolveSheetSizeRequestRetryOrgId,
  SheetSizeRequestOutcome,
} from "../utils/functions/oversizeSheet";

// Oversize sheet guard: the ONE "Request new sheet size" sender, shared by
// the dialog host (<OversizeSheetDialogHost/>) and the inline request that
// replaces "Add to order" on an oversize logo (flag
// REACT_APP_OVERSIZE_SHEET_INLINE_REQUEST_ENABLED) - so both send exactly
// the same call(s): one POST organizations/{orgId}/logos/{logoId}/
// sheet-size-request per logo, sequentially, each under an 8 s abort and
// the caller's loop abort, { designId } as the body when the logo belongs
// to a design, the org from resolveSheetSizeRequestOrgId and ONE retry
// under the logo's own org after a 404 (resolveSheetSizeRequestRetryOrgId).
// Extracted verbatim from the dialog host (28/9); the host's behaviour is
// unchanged.

export const OUTCOME_TOAST_KEY: Record<SheetSizeRequestOutcome, string> = {
  sent: "oversizeSheet.requestSent",
  alreadyRequested: "oversizeSheet.alreadyRequested",
  dailyCapReached: "oversizeSheet.tryLater",
  notConfigured: "oversizeSheet.notConfigured",
  notApplicable: "oversizeSheet.notApplicable",
  requestFailed: "oversizeSheet.requestFailed",
};

/** One request call's outcome before it is sorted into a toast. */
type SheetSizeRequestAttempt =
  | { ok: true; result: { sent?: unknown; reason?: unknown } | null | undefined }
  | { ok: false; error: unknown };

export interface SheetSizeRequestHandlers {
  /** The API sent (or already had) the request for this logo: remember it. */
  onRequested: (logoId: number) => void;
  /** The API says requests are not configured: hide the request action. */
  onNotConfigured: () => void;
}

export type SheetSizeRequestSender = (
  pending: OversizeLogoInfo[],
  loopSignal: AbortSignal,
  handlers: SheetSizeRequestHandlers
) => Promise<void>;

/**
 * Returns the loop that sends `pending` (already filtered to not-yet-
 * requested and capped by the caller). Aborting `loopSignal` cancels the
 * in-flight call, sends nothing more and shows no toast afterwards - a 202
 * that had already arrived is still reported through onRequested.
 */
export function useSheetSizeRequestSender(): SheetSizeRequestSender {
  const { t } = useTranslation();
  const { requestSheetSize } = useApiClient();
  const { organizationId: profileOrgId, role } = useProfileContext();
  const { organizationId: actAsOrgId } = useOrganizationImpersonationContext();
  const isAdmin = role === Role.Admin;

  // One POST under `orgId`, under the 8 s abort and the loop's abort.
  const postOne = async (
    orgId: number,
    logo: OversizeLogoInfo,
    loopSignal: AbortSignal
  ): Promise<SheetSizeRequestAttempt> => {
    const controller = new AbortController();
    const onLoopAbort = () => controller.abort();
    if (loopSignal.aborted) controller.abort();
    else loopSignal.addEventListener("abort", onLoopAbort);
    const timer = setTimeout(() => controller.abort(), OVERSIZE_SHEET_REQUEST_TIMEOUT_MS);
    try {
      const result = await requestSheetSize(
        orgId,
        logo.logoId,
        controller.signal,
        logo.designId !== undefined && logo.designId > 0
          ? { designId: logo.designId }
          : undefined
      );
      return { ok: true, result };
    } catch (error) {
      return { ok: false, error };
    } finally {
      clearTimeout(timer);
      loopSignal.removeEventListener("abort", onLoopAbort);
    }
  };

  const sendOne = async (
    logo: OversizeLogoInfo,
    loopSignal: AbortSignal
  ): Promise<SheetSizeRequestOutcome> => {
    const orgId = resolveSheetSizeRequestOrgId(logo.orgId, actAsOrgId, profileOrgId, isAdmin);
    if (!(orgId > 0)) return "requestFailed";
    let attempt = await postOne(orgId, logo, loopSignal);
    // 404 under the session's org: the logo may be one the session can see
    // without being linked to its org (sharing relation, parent customer,
    // design fallback) - one retry under the logo's own org, error path only.
    const retryOrgId = resolveSheetSizeRequestRetryOrgId(orgId, logo.orgId, isAdmin);
    if (
      retryOrgId > 0 &&
      !loopSignal.aborted &&
      !attempt.ok &&
      isSheetSizeRequestNotFound(attempt.error)
    ) {
      attempt = await postOne(retryOrgId, logo, loopSignal);
    }
    return attempt.ok
      ? classifySheetSizeRequestOutcome(attempt.result, undefined)
      : classifySheetSizeRequestOutcome(undefined, attempt.error);
  };

  return async (pending, loopSignal, handlers) => {
    for (const logo of pending) {
      if (loopSignal.aborted) break;
      const outcome = await sendOne(logo, loopSignal);
      if (loopSignal.aborted) {
        // Closed while the call was out: keep the fact when the API did
        // send it, but no toast and no further calls.
        if (outcome === "sent" || outcome === "alreadyRequested") {
          handlers.onRequested(logo.logoId);
        }
        break;
      }
      const text = t(OUTCOME_TOAST_KEY[outcome]);
      switch (outcome) {
        case "sent":
          handlers.onRequested(logo.logoId);
          showSuccessToast(text);
          break;
        case "alreadyRequested":
          handlers.onRequested(logo.logoId);
          showInfoToast(text);
          break;
        case "dailyCapReached":
          showWarningToast(text);
          break;
        case "notConfigured":
          handlers.onNotConfigured();
          showInfoToast(text);
          break;
        case "notApplicable":
          showInfoToast(text);
          break;
        default:
          showErrorToast(text);
          break;
      }
      // Requests are not configured: the remaining calls would answer the same.
      if (outcome === "notConfigured") break;
    }
  };
}
